/**
 * Configuration métier du funnel "Diagnostic CEE agricole".
 *
 * Couvre :
 *  - Les orientations d'exploitation (cards de l'étape 1)
 *  - Les types de serres, d'animaux et de stockage (étape 3)
 *  - Les catégories de travaux envisagés (étape 4) avec mapping vers fiches CEE
 */

import {
  Beef,
  Box,
  Carrot,
  Egg,
  Flame,
  Flower,
  Fuel,
  Gauge,
  Lightbulb,
  Milk,
  Package,
  Snowflake,
  Sprout,
  Tractor,
  Trees,
  TreePine,
  Warehouse,
  Wheat,
  Wind,
  Wine,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────
   Orientation principale de l'exploitation
───────────────────────────────────────────────────────── */

export type OrientationId =
  | 'grandes-cultures'
  | 'elevage'
  | 'maraichage-horticulture'
  | 'viticulture-arboriculture'
  | 'polyculture-elevage';

export interface OrientationMeta {
  id: OrientationId;
  label: string;
  sublabel: string;
  icon: LucideIcon;
  /** Indique quelles sections d'infrastructure pertinentes (révélation conditionnelle). */
  reveals: {
    serres: boolean;
    elevage: boolean;
    stockage: boolean; // toujours présenté mais selon orientation, défaut peut changer
  };
  /** Fiches CEE typiquement applicables (codes utilisés pour scorer le résultat). */
  ceeBoost: string[];
}

export const ORIENTATIONS: OrientationMeta[] = [
  {
    id: 'grandes-cultures',
    label: 'Grandes cultures / céréales',
    sublabel: "Blé, maïs, colza, tournesol, soja, oléagineux…",
    icon: Wheat,
    reveals: { serres: false, elevage: false, stockage: true },
    ceeBoost: ['AGRI-EQ-106', 'AGRI-EQ-105', 'AGRI-SE-101', 'AGRI-EQ-110'],
  },
  {
    id: 'elevage',
    label: 'Élevage',
    sublabel: 'Bovins, porcins, volailles, ovins, caprins',
    icon: Beef,
    reveals: { serres: false, elevage: true, stockage: true },
    ceeBoost: ['AGRI-TH-103', 'AGRI-TH-104', 'AGRI-TH-105', 'AGRI-TH-113', 'AGRI-TH-108'],
  },
  {
    id: 'maraichage-horticulture',
    label: 'Maraîchage / horticulture',
    sublabel: 'Légumes, plantes, fleurs — sous serre ou plein champ',
    icon: Flower,
    reveals: { serres: true, elevage: false, stockage: true },
    ceeBoost: [
      'AGRI-EQ-101',
      'AGRI-EQ-102',
      'AGRI-EQ-104',
      'AGRI-EQ-107',
      'AGRI-EQ-108',
      'AGRI-EQ-109',
      'AGRI-TH-109',
      'AGRI-TH-110',
      'AGRI-TH-117',
      'AGRI-TH-118',
      'AGRI-TH-119',
    ],
  },
  {
    id: 'viticulture-arboriculture',
    label: 'Viticulture / arboriculture',
    sublabel: 'Vignes, vergers, fruits — production et chai',
    icon: Wine,
    reveals: { serres: false, elevage: false, stockage: true },
    ceeBoost: ['AGRI-UT-101', 'AGRI-UT-102', 'AGRI-UT-103', 'AGRI-UT-104'],
  },
  {
    id: 'polyculture-elevage',
    label: 'Polyculture-élevage',
    sublabel: 'Exploitation mixte cultures + animaux',
    icon: TreePine,
    reveals: { serres: true, elevage: true, stockage: true },
    ceeBoost: ['AGRI-TH-108', 'AGRI-EQ-106', 'AGRI-TH-103'],
  },
];

export function findOrientation(id: OrientationId): OrientationMeta | undefined {
  return ORIENTATIONS.find((o) => o.id === id);
}

/* ─────────────────────────────────────────────────────────
   Serres
───────────────────────────────────────────────────────── */

export type SerresType = 'maraichere' | 'horticole' | 'mixte' | 'autre';
export type SerresCouverture = 'verre' | 'polycarbonate' | 'film-plastique' | 'tunnel';
export type SerresChauffageCombustible =
  | 'gaz'
  | 'fuel'
  | 'granules'
  | 'biomasse'
  | 'electricite'
  | 'autre';

export const SERRES_TYPES: Array<{
  id: SerresType;
  label: string;
  sublabel: string;
  icon: LucideIcon;
}> = [
  { id: 'maraichere', label: 'Maraîchère', sublabel: 'Tomates, salades, fraises…', icon: Carrot },
  { id: 'horticole', label: 'Horticole', sublabel: 'Fleurs coupées, plantes en pot', icon: Flower },
  { id: 'mixte', label: 'Mixte', sublabel: 'Maraîchère + horticole', icon: Sprout },
  { id: 'autre', label: 'Autre', sublabel: 'Pépinière, semences, autres', icon: Trees },
];

export const SERRES_COUVERTURES: Array<{
  id: SerresCouverture;
  label: string;
  sublabel: string;
}> = [
  { id: 'verre', label: 'Verre', sublabel: 'Serre fermée vitrée' },
  { id: 'polycarbonate', label: 'Polycarbonate', sublabel: 'Plaques alvéolaires' },
  { id: 'film-plastique', label: 'Film plastique', sublabel: 'Serre fermée bâche PE' },
  { id: 'tunnel', label: 'Tunnel', sublabel: 'Tunnel plastique non chauffé' },
];

export const SERRES_COMBUSTIBLES: Array<{
  id: SerresChauffageCombustible;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'gaz', label: 'Gaz', icon: Flame },
  { id: 'fuel', label: 'Fuel / fioul', icon: Fuel },
  { id: 'granules', label: 'Granulés bois', icon: Trees },
  { id: 'biomasse', label: 'Biomasse / bois', icon: Trees },
  { id: 'electricite', label: 'Électricité', icon: Zap },
  { id: 'autre', label: 'Autre', icon: Wrench },
];

/* ─────────────────────────────────────────────────────────
   Élevage
───────────────────────────────────────────────────────── */

export type ElevageType =
  | 'bovin-lait'
  | 'bovin-viande'
  | 'porcin'
  | 'volaille'
  | 'ovin-caprin'
  | 'mixte';

export const ELEVAGE_TYPES: Array<{
  id: ElevageType;
  label: string;
  sublabel: string;
  icon: LucideIcon;
}> = [
  {
    id: 'bovin-lait',
    label: 'Bovins laitiers',
    sublabel: 'Production de lait, tank et salle de traite',
    icon: Milk,
  },
  {
    id: 'bovin-viande',
    label: 'Bovins viande',
    sublabel: 'Engraissement, naisseur-engraisseur',
    icon: Beef,
  },
  {
    id: 'porcin',
    label: 'Porcins',
    sublabel: 'Naissage, engraissement',
    icon: Beef,
  },
  {
    id: 'volaille',
    label: 'Volailles',
    sublabel: 'Poulets, poules pondeuses, canards',
    icon: Egg,
  },
  {
    id: 'ovin-caprin',
    label: 'Ovins / caprins',
    sublabel: 'Moutons, chèvres — lait ou viande',
    icon: Beef,
  },
  {
    id: 'mixte',
    label: 'Élevage mixte',
    sublabel: 'Plusieurs espèces sur la même exploitation',
    icon: Beef,
  },
];

/* ─────────────────────────────────────────────────────────
   Stockage
───────────────────────────────────────────────────────── */

export type StockageUsage =
  | 'materiel-agricole'
  | 'cereales-grains'
  | 'foin-paille'
  | 'fruits-legumes'
  | 'vin-spiritueux'
  | 'lait-fromage'
  | 'intrants';

export interface StockageUsageMeta {
  id: StockageUsage;
  label: string;
  sublabel: string;
  /** "commercial" = produit destiné à la revente / "operationnel" = pour l'activité */
  vocation: 'commercial' | 'operationnel';
  icon: LucideIcon;
  /** Indique si le stockage est typiquement réfrigéré. */
  refrigereDefault: boolean;
}

export const STOCKAGE_USAGES: StockageUsageMeta[] = [
  {
    id: 'materiel-agricole',
    label: 'Matériel agricole',
    sublabel: "Tracteurs, moissonneuses, outils — pour l'activité",
    vocation: 'operationnel',
    icon: Tractor,
    refrigereDefault: false,
  },
  {
    id: 'cereales-grains',
    label: 'Céréales / grains',
    sublabel: 'Silos, vrac — à revendre (commercial)',
    vocation: 'commercial',
    icon: Wheat,
    refrigereDefault: false,
  },
  {
    id: 'foin-paille',
    label: 'Foin / paille',
    sublabel: 'Hangar de stockage fourrager',
    vocation: 'operationnel',
    icon: Package,
    refrigereDefault: false,
  },
  {
    id: 'fruits-legumes',
    label: 'Fruits / légumes',
    sublabel: 'Conservation post-récolte — vente commerciale',
    vocation: 'commercial',
    icon: Carrot,
    refrigereDefault: true,
  },
  {
    id: 'vin-spiritueux',
    label: 'Vin / spiritueux',
    sublabel: 'Chai, cuverie, cave — production transformée',
    vocation: 'commercial',
    icon: Wine,
    refrigereDefault: true,
  },
  {
    id: 'lait-fromage',
    label: 'Produits laitiers / fromages',
    sublabel: 'Tank, hâloir, cave — transformation à la ferme',
    vocation: 'commercial',
    icon: Milk,
    refrigereDefault: true,
  },
  {
    id: 'intrants',
    label: 'Intrants',
    sublabel: 'Engrais, semences, produits phyto',
    vocation: 'operationnel',
    icon: Box,
    refrigereDefault: false,
  },
];

/* ─────────────────────────────────────────────────────────
   Équipements / utilités complémentaires
───────────────────────────────────────────────────────── */

export type EquipementId =
  | 'pompage-irrigation'
  | 'sechage-grain'
  | 'sechage-fourrage'
  | 'variateurs-vitesse'
  | 'eclairage'
  | 'froid-commercial'
  | 'aucun';

export const EQUIPEMENTS: Array<{
  id: EquipementId;
  label: string;
  sublabel: string;
  icon: LucideIcon;
  /** Code CEE typique débloqué quand cet équipement est présent. */
  ceeCodes: string[];
}> = [
  {
    id: 'pompage-irrigation',
    label: 'Pompage / irrigation',
    sublabel: 'Moteurs pompes, asperseurs',
    icon: Gauge,
    ceeCodes: ['AGRI-UT-101', 'AGRI-UT-102'],
  },
  {
    id: 'sechage-grain',
    label: 'Séchage de grain',
    sublabel: 'Cellules de séchage à chaud / air',
    icon: Wheat,
    ceeCodes: ['AGRI-EQ-110', 'AGRI-EQ-106'],
  },
  {
    id: 'sechage-fourrage',
    label: 'Séchage de fourrage',
    sublabel: "Séchage solaire en grange",
    icon: Sprout,
    ceeCodes: ['AGRI-EQ-110'],
  },
  {
    id: 'variateurs-vitesse',
    label: 'Variateurs de vitesse',
    sublabel: 'Moteurs asynchrones, ventilation, pompage',
    icon: Wrench,
    ceeCodes: ['AGRI-UT-101', 'AGRI-UT-102'],
  },
  {
    id: 'eclairage',
    label: 'Éclairage à moderniser',
    sublabel: 'Incandescent, fluo → LED (bâtiments, serres)',
    icon: Lightbulb,
    ceeCodes: [],
  },
  {
    id: 'froid-commercial',
    label: 'Production de froid',
    sublabel: 'Groupes de froid (fromagerie, chai, fruits)',
    icon: Snowflake,
    ceeCodes: ['AGRI-UT-103', 'AGRI-UT-104'],
  },
  {
    id: 'aucun',
    label: 'Aucun de ces équipements',
    sublabel: 'Passer à l\'étape suivante',
    icon: Wind,
    ceeCodes: [],
  },
];

/* ─────────────────────────────────────────────────────────
   Catégories de travaux envisagés
───────────────────────────────────────────────────────── */

export type TravauxAgriCategory =
  | 'serres'
  | 'elevage'
  | 'stockage'
  | 'energie'
  | 'utilites';

export interface TravauxAgriCategoryMeta {
  id: TravauxAgriCategory;
  label: string;
  sublabel: string;
  icon: LucideIcon;
}

export const TRAVAUX_AGRI_CATEGORIES: TravauxAgriCategoryMeta[] = [
  {
    id: 'serres',
    label: 'Serres horticoles',
    sublabel: 'Isolation, écrans thermiques, régulation climatique',
    icon: Flower,
  },
  {
    id: 'elevage',
    label: 'Bâtiments d\'élevage',
    sublabel: 'PAC, ventilation, récupération de chaleur',
    icon: Beef,
  },
  {
    id: 'stockage',
    label: 'Stockage & séchage',
    sublabel: 'Silos, séchage, régulation grain',
    icon: Warehouse,
  },
  {
    id: 'energie',
    label: 'Production d\'énergie / chaleur',
    sublabel: 'Chaudière, biomasse, solaire',
    icon: Flame,
  },
  {
    id: 'utilites',
    label: 'Utilités industrielles',
    sublabel: 'Variateurs vitesse, froid, éclairage',
    icon: Zap,
  },
];

/* ─────────────────────────────────────────────────────────
   Travaux unitaires
───────────────────────────────────────────────────────── */

export interface TravauxAgriOption {
  id: string;
  category: TravauxAgriCategory;
  label: string;
  sublabel: string;
  /** Code(s) CEE associé(s) à ce poste de travaux. */
  ceeCodes: string[];
  /** Estimation de prime min/max (€) — calibré sur marché EMMY 2026. */
  primeMin: number;
  primeMax: number;
}

export const TRAVAUX_AGRI: TravauxAgriOption[] = [
  /* — Serres — */
  {
    id: 'serre-double-ecran',
    category: 'serres',
    label: 'Double écran thermique',
    sublabel: 'Réduction des déperditions nocturnes',
    ceeCodes: ['AGRI-EQ-102'],
    primeMin: 800,
    primeMax: 3500,
  },
  {
    id: 'serre-ecrans-lateraux',
    category: 'serres',
    label: 'Écrans thermiques latéraux',
    sublabel: 'Parois latérales isolées',
    ceeCodes: ['AGRI-EQ-104'],
    primeMin: 400,
    primeMax: 2000,
  },
  {
    id: 'serre-isolation-parois',
    category: 'serres',
    label: 'Isolation des parois de serre',
    sublabel: 'Doublage isolant',
    ceeCodes: ['AGRI-EQ-107'],
    primeMin: 600,
    primeMax: 3000,
  },
  {
    id: 'serre-couverture',
    category: 'serres',
    label: 'Couverture performante',
    sublabel: 'Vitrage hi-tech, polycarbonate alvéolaire',
    ceeCodes: ['AGRI-EQ-109'],
    primeMin: 1000,
    primeMax: 4500,
  },
  {
    id: 'serre-regulation',
    category: 'serres',
    label: 'Régulation climatique informatisée',
    sublabel: 'Ordinateur climatique + intégration température',
    ceeCodes: ['AGRI-EQ-101'],
    primeMin: 300,
    primeMax: 1500,
  },
  {
    id: 'serre-stockage-eau',
    category: 'serres',
    label: "Stockage d'eau bioclimatique",
    sublabel: 'Tampon thermique pour serres chauffées',
    ceeCodes: ['AGRI-EQ-108'],
    primeMin: 500,
    primeMax: 2500,
  },
  {
    id: 'serre-deshumidificateur',
    category: 'serres',
    label: 'Déshumidificateur thermodynamique',
    sublabel: 'Régulation hygrométrique + récupération chaleur',
    ceeCodes: ['AGRI-TH-117', 'AGRI-TH-119'],
    primeMin: 800,
    primeMax: 3200,
  },

  /* — Élevage — */
  {
    id: 'elev-pac',
    category: 'elevage',
    label: 'Pompe à chaleur air/eau ou eau/eau',
    sublabel: 'Chauffage bâtiment ou eau de process',
    ceeCodes: ['AGRI-TH-108'],
    primeMin: 1500,
    primeMax: 5500,
  },
  {
    id: 'elev-recup-tank',
    category: 'elevage',
    label: 'Récupérateur de chaleur sur tank à lait',
    sublabel: 'ECS pré-chauffée gratuitement',
    ceeCodes: ['AGRI-TH-105'],
    primeMin: 600,
    primeMax: 2200,
  },
  {
    id: 'elev-prerefroidisseur',
    category: 'elevage',
    label: 'Pré-refroidisseur de lait',
    sublabel: 'Échangeur eau/lait avant tank',
    ceeCodes: ['AGRI-TH-103'],
    primeMin: 500,
    primeMax: 1800,
  },
  {
    id: 'elev-recup-froid',
    category: 'elevage',
    label: 'Récupération chaleur sur groupe froid',
    sublabel: 'Chambres froides, ateliers laitiers',
    ceeCodes: ['AGRI-TH-104'],
    primeMin: 700,
    primeMax: 2800,
  },
  {
    id: 'elev-echangeur-volaille',
    category: 'elevage',
    label: 'Échangeur air/air en bâtiment volaille',
    sublabel: 'Récupération chaleur ventilation',
    ceeCodes: ['AGRI-TH-113'],
    primeMin: 500,
    primeMax: 2500,
  },

  /* — Stockage — */
  {
    id: 'stock-regulation-silo',
    category: 'stockage',
    label: 'Régulation ventilation silos / vrac',
    sublabel: 'Optimisation conservation céréales',
    ceeCodes: ['AGRI-EQ-106'],
    primeMin: 400,
    primeMax: 2000,
  },
  {
    id: 'stock-sechage-solaire',
    category: 'stockage',
    label: 'Séchage solaire par insufflation',
    sublabel: 'Panneaux solaires hybrides + ventilateurs',
    ceeCodes: ['AGRI-EQ-110'],
    primeMin: 1000,
    primeMax: 4500,
  },

  /* — Énergie / chaleur — */
  {
    id: 'energie-chaudiere-serre',
    category: 'energie',
    label: 'Chaudière haute performance pour serre',
    sublabel: 'Condensation gaz / biomasse',
    ceeCodes: ['AGRI-TH-110'],
    primeMin: 1200,
    primeMax: 5000,
  },
  {
    id: 'energie-stockage-chaud',
    category: 'energie',
    label: "Stockage d'eau chaude (Open Buffer)",
    sublabel: 'Optimisation chaudière biomasse / cogénération',
    ceeCodes: ['AGRI-TH-101', 'AGRI-TH-102'],
    primeMin: 600,
    primeMax: 2800,
  },
  {
    id: 'energie-recup-condensation',
    category: 'energie',
    label: 'Récupérateur de chaleur à condensation',
    sublabel: 'Sur chaudière de serre',
    ceeCodes: ['AGRI-TH-109'],
    primeMin: 700,
    primeMax: 3000,
  },

  /* — Utilités — */
  {
    id: 'util-variateur-synchrone',
    category: 'utilites',
    label: 'Moto-variateur synchrone',
    sublabel: 'Aimants permanents ou reluctance',
    ceeCodes: ['AGRI-UT-101'],
    primeMin: 400,
    primeMax: 1800,
  },
  {
    id: 'util-variateur-async',
    category: 'utilites',
    label: 'Variation vitesse moteur asynchrone',
    sublabel: 'Sur pompage, ventilation existante',
    ceeCodes: ['AGRI-UT-102'],
    primeMin: 300,
    primeMax: 1500,
  },
  {
    id: 'util-bp-flottante',
    category: 'utilites',
    label: 'Régulation basse pression flottante (froid)',
    sublabel: 'Sur groupes de production de froid',
    ceeCodes: ['AGRI-UT-103'],
    primeMin: 500,
    primeMax: 2200,
  },
  {
    id: 'util-hp-flottante',
    category: 'utilites',
    label: 'Régulation haute pression flottante (froid)',
    sublabel: 'Sur groupes de production de froid',
    ceeCodes: ['AGRI-UT-104'],
    primeMin: 500,
    primeMax: 2200,
  },
];

export function getTravauxByCategory(category: TravauxAgriCategory): TravauxAgriOption[] {
  return TRAVAUX_AGRI.filter((t) => t.category === category);
}

export function findTravauxAgri(id: string): TravauxAgriOption | undefined {
  return TRAVAUX_AGRI.find((t) => t.id === id);
}
